import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AnalyticsService } from '../../@core/utils';
import { CharacterLimits } from '../../enum/assignment';
import { ToasterService } from '../../services/toaster.service';
import { UtilService } from '../../services/util.service';

@Component({
  selector: 'ngx-rename-pdf-dialog',
  templateUrl: './rename-pdf-dialog.component.html',
  styleUrls: ['./rename-pdf-dialog.component.scss'],
})
export class RenamePdfDialogComponent {
  fileName: string = '';
  isRenameSelected: boolean = false;
  characterLimit = CharacterLimits.FILE_NAME;
  isLimitReached = false;
  previewFileName: string = '';
  hasMultipleSpaces = false;

  constructor(
    public dialogRef: MatDialogRef<RenamePdfDialogComponent>,
    public analyticsService: AnalyticsService,
    private toasterService: ToasterService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private utilService: UtilService,
  ) {
    // Initialize file name with default value passed from parent
    this.fileName = data.defaultFileName;
    this.previewFileName = this.fileName;
  }

  /**
   * @description Closes the dialog when user cancels the action.
   * Sends a flag to the parent component indicating that
   * the download process should be aborted.
   */
  onCancel(): void {
    this.dialogRef.close({
      isCancel: true,
    });
  }

  /**
   * @description Handles the download action.
   * - Validates the file name if user selects rename option
   * - Ensures file name is not empty and within 100 character limit
   * - If validation passes, returns the final file name to parent component
   * - If rename is not selected, returns the default file name
   */
  onDownload(): void {
    if (this.isRenameSelected) {
      const fileName = this.utilService.validateFileName(this.fileName);

      if (!fileName.valid) {
        this.toasterService.showToast(fileName.error, 'warning');
        return;
      }

      // Prevent multiple consecutive spaces
      if (this.hasMultipleSpaces) {
        this.toasterService.showToast(
          'Only a single space is allowed between characters.',
          'warning',
        );
        return;
      }
    }

    this.dialogRef.close({
      isCancel: false,
      fileName: this.isRenameSelected
        ? this.previewFileName.trim() // Use the generated preview name which is already formatted
        : this.data.defaultFileName,
    });
  }

  /**
   * @description Handles real-time validation and preview generation as user types the file name.
   * - Validates length and multiple spaces on each input change
   * - Updates the preview file name only if the current input is valid
   * - Prevents preview generation if there are multiple consecutive spaces
   */
  onFileNameChange(): void {
    const value = this.fileName || '';
    const length = value.length;

    // Length validation
    this.isLimitReached = length >= this.characterLimit;

    // Multiple space validation
    this.hasMultipleSpaces = /\s{2,}/.test(value);

    // Generate preview only if valid
    this.previewFileName = !this.hasMultipleSpaces
      ? this.generatePreview(value)
      : '';
  }

  /**
   * @description Generates a preview of the final file name based on user input.
   * - Trims leading/trailing spaces and collapses multiple spaces into a single space
   * - Removes invalid characters while allowing letters, numbers, hyphens, underscores, and spaces
   * - Appends "-with-answer" or "-without-answer" based on the PDF type
   * @param name
   * @returns filename with appropriate suffix or empty string if input is invalid
   */
  generatePreview(name: string): string {
    const formatted = name
      .trim()
      .replace(/\s+/g, ' ') // collapse multiple spaces → single space
      .replace(/[^a-zA-Z0-9 _-]/g, ''); // allow only valid chars

    return formatted
      ? `${formatted}-${this.data.generatePdfWithAnswer ? 'with-answer' : 'without-answer'}`
      : '';
  }
}
